import { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import { Plus, Edit3, Trash2, X, Check, Image, Film, Mic, FileText, File, FileSpreadsheet, MessageSquare, Database, Zap, Users, GitBranch, BookOpen, Hash, Activity } from 'lucide-react';
import toast from 'react-hot-toast';

const ALL_MEDIA_TYPES = [
  { key: 'image', label: 'Images', icon: <Image size={12} /> },
  { key: 'video', label: 'Videos', icon: <Film size={12} /> },
  { key: 'audio', label: 'Audio', icon: <Mic size={12} /> },
  { key: 'pdf', label: 'PDF', icon: <FileText size={12} /> },
  { key: 'document', label: 'Docs', icon: <File size={12} /> },
  { key: 'excel', label: 'Excel', icon: <FileSpreadsheet size={12} /> },
  { key: 'csv', label: 'CSV', icon: <FileText size={12} /> },
];

const ALL_CHANNELS = [
  { key: 'whatsapp', label: 'WhatsApp', color: '#25D366' },
  { key: 'telegram', label: 'Telegram', color: '#0088cc' },
  { key: 'facebook', label: 'Facebook', color: '#1877F2' },
  { key: 'instagram', label: 'Instagram', color: '#E4405F' },
  { key: 'kakaotalk', label: 'KakaoTalk', color: '#3C1E1E' },
  { key: 'tiktok', label: 'TikTok', color: '#000000' },
  { key: 'webchat', label: 'Web Chat', color: '#6366f1' },
];

const ALL_DB_TYPES = [
  { key: 'mysql', label: 'MySQL', color: '#4479A1' },
  { key: 'postgresql', label: 'PostgreSQL', color: '#336791' },
  { key: 'mssql', label: 'SQL Server', color: '#CC2927' },
  { key: 'mongodb', label: 'MongoDB', color: '#47A248' },
  { key: 'sqlite', label: 'SQLite', color: '#003B57' },
];

const PLAN_COLORS = ['#6366f1', '#0ea5e9', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

const emptyChannelTypeLimits = { whatsapp: 0, telegram: 0, facebook: 0, instagram: 0, kakaotalk: 0, tiktok: 0, webchat: 0 };

const emptyPlan = {
  name: '', slug: '', description: '',
  price: { monthly: '', yearly: '', currency: 'MYR' },
  limits: { messagesPerMonth: '', aiRepliesPerMonth: '', maxAgents: '', maxChannels: '', maxWorkflows: 1, maxPrompts: 1 },
  features: '',
  allowedMediaTypes: ['image', 'audio', 'pdf', 'document'],
  allowedChannels: ['whatsapp', 'telegram', 'facebook', 'instagram', 'webchat'],
  allowedDbTypes: [],
  channelTypeLimits: { ...emptyChannelTypeLimits },
  isActive: true,
  sortOrder: 0,
};

const formatPrice = (sen) => {
  if (sen === 0 || sen === '0') return 'Free';
  if (!sen) return 'Free';
  return `RM ${(Number(sen) / 100).toFixed(2)}`;
};

const limitVal = (v) => (v === -1 ? '∞' : v?.toLocaleString() ?? '0');

export function AdminPlans() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);

  useEffect(() => { fetchPlans(); }, []);

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const { data } = await adminAPI.getPlans();
      setPlans(data.data);
    } catch (_) {}
    finally { setLoading(false); }
  };

  const openCreate = () => { setEditingPlan(null); setShowModal(true); };
  const openEdit = (plan) => { setEditingPlan(plan); setShowModal(true); };

  const handleDelete = async (plan) => {
    if (!window.confirm(`Delete plan "${plan.name}"? This cannot be undone.`)) return;
    try {
      await adminAPI.deletePlan(plan._id);
      toast.success('Plan deleted');
      fetchPlans();
    } catch (_) {}
  };

  const handleSaved = () => { setShowModal(false); setEditingPlan(null); fetchPlans(); };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Subscription Plans</h1>
          <p className="page-subtitle">{plans.length} plan{plans.length !== 1 ? 's' : ''} &mdash; Currency: MYR</p>
        </div>
        <button className="btn btn-primary" onClick={openCreate}><Plus size={16} /> Create Plan</button>
      </div>

      <div className="page-body">
        {loading ? (
          <div className="flex-center" style={{ padding: 60 }}><div className="spinner" /></div>
        ) : plans.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: 56 }}>
            <div style={{ width: 64, height: 64, borderRadius: 16, background: 'var(--primary-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <Zap size={28} style={{ color: 'var(--primary)' }} />
            </div>
            <h3 style={{ marginBottom: 8 }}>No Plans Yet</h3>
            <p style={{ color: 'var(--text-muted)', marginBottom: 20, maxWidth: 320, margin: '0 auto 20px' }}>
              Create subscription plans to offer to your merchants. Set pricing, limits, and feature access.
            </p>
            <button className="btn btn-primary" onClick={openCreate}><Plus size={16} /> Create First Plan</button>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 20 }}>
            {plans.map((plan, idx) => {
              const accentColor = PLAN_COLORS[idx % PLAN_COLORS.length];
              const isFree = !plan.price?.monthly || plan.price.monthly === 0;
              return (
                <div key={plan._id} className="card" style={{ padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', border: `1px solid var(--border)` }}>
                  {/* Card Header */}
                  <div style={{ background: accentColor, padding: '20px 20px 16px', position: 'relative' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <h3 style={{ margin: 0, color: '#fff', fontSize: '1.1rem', fontWeight: 700 }}>{plan.name}</h3>
                        <span style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.65)', fontFamily: 'monospace' }}>{plan.slug}</span>
                      </div>
                      <span style={{
                        fontSize: '0.7rem', fontWeight: 600, padding: '3px 8px', borderRadius: 20,
                        background: plan.isActive ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.25)',
                        color: '#fff', border: '1px solid rgba(255,255,255,0.3)',
                      }}>
                        {plan.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>

                    {/* Price */}
                    <div style={{ marginTop: 14 }}>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                        {isFree ? (
                          <span style={{ fontSize: '2rem', fontWeight: 800, color: '#fff' }}>Free</span>
                        ) : (
                          <>
                            <span style={{ fontSize: '1rem', fontWeight: 600, color: 'rgba(255,255,255,0.8)', marginTop: 6 }}>RM</span>
                            <span style={{ fontSize: '2rem', fontWeight: 800, color: '#fff' }}>
                              {(plan.price.monthly / 100).toFixed(2)}
                            </span>
                            <span style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.7)' }}>/mo</span>
                          </>
                        )}
                      </div>
                      {plan.price?.yearly > 0 && (
                        <div style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.65)', marginTop: 2 }}>
                          {formatPrice(plan.price.yearly)} / year
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Card Body */}
                  <div style={{ padding: '16px 20px', flex: 1, display: 'flex', flexDirection: 'column', gap: 14 }}>
                    {plan.description && (
                      <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', margin: 0 }}>{plan.description}</p>
                    )}

                    {/* Limits Grid */}
                    <div>
                      <div style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: '0.05em', marginBottom: 8 }}>Plan Limits</div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                        {[
                          { icon: <MessageSquare size={12} />, label: 'Messages', val: limitVal(plan.limits?.messagesPerMonth) },
                          { icon: <Zap size={12} />, label: 'AI Replies', val: limitVal(plan.limits?.aiRepliesPerMonth) },
                          { icon: <Users size={12} />, label: 'Agents', val: limitVal(plan.limits?.maxAgents) },
                          { icon: <Hash size={12} />, label: 'Channels', val: limitVal(plan.limits?.maxChannels) },
                          { icon: <GitBranch size={12} />, label: 'Workflows', val: limitVal(plan.limits?.maxWorkflows ?? 1) },
                          { icon: <BookOpen size={12} />, label: 'Prompts', val: limitVal(plan.limits?.maxPrompts ?? 1) },
                        ].map(({ icon, label, val }) => (
                          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 8px', borderRadius: 6, background: 'var(--bg)', border: '1px solid var(--border)' }}>
                            <span style={{ color: accentColor }}>{icon}</span>
                            <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{label}</span>
                            <span style={{ fontSize: '0.78rem', fontWeight: 700, marginLeft: 'auto' }}>{val}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Features */}
                    {plan.features?.length > 0 && (
                      <div>
                        <div style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: '0.05em', marginBottom: 8 }}>Features</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                          {plan.features.map((f, i) => (
                            <div key={i} style={{ fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 7 }}>
                              <div style={{ width: 16, height: 16, borderRadius: '50%', background: accentColor + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <Check size={10} style={{ color: accentColor }} />
                              </div>
                              {f}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Channels */}
                    {plan.allowedChannels?.length > 0 && (
                      <div>
                        <div style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: '0.05em', marginBottom: 6 }}>Channels</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                          {ALL_CHANNELS.filter(ch => plan.allowedChannels.includes(ch.key)).map(ch => {
                            const typeLimit = plan.channelTypeLimits?.[ch.key] || 0;
                            return (
                              <span key={ch.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 20, fontSize: '0.7rem', fontWeight: 600, background: ch.color + '15', color: ch.color, border: `1px solid ${ch.color}35` }}>
                                <span style={{ width: 6, height: 6, borderRadius: '50%', background: ch.color, display: 'inline-block' }} />
                                {ch.label}
                                {typeLimit > 0 && <span style={{ background: ch.color, color: '#fff', borderRadius: 10, padding: '0 4px', fontSize: '0.62rem', fontWeight: 700 }}>max {typeLimit}</span>}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* DB Types */}
                    {plan.allowedDbTypes?.length > 0 && (
                      <div>
                        <div style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: '0.05em', marginBottom: 6 }}>Databases</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                          {ALL_DB_TYPES.filter(db => plan.allowedDbTypes.includes(db.key)).map(db => (
                            <span key={db.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 20, fontSize: '0.7rem', fontWeight: 600, background: db.color + '15', color: db.color, border: `1px solid ${db.color}35` }}>
                              <Database size={9} /> {db.label}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Media Types */}
                    {plan.allowedMediaTypes?.length > 0 && (
                      <div>
                        <div style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: '0.05em', marginBottom: 6 }}>Media</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                          {ALL_MEDIA_TYPES.map(mt => {
                            const isOn = plan.allowedMediaTypes.includes(mt.key);
                            return (
                              <span key={mt.key} style={{
                                display: 'inline-flex', alignItems: 'center', gap: 3, padding: '2px 8px', borderRadius: 20,
                                fontSize: '0.7rem', fontWeight: 500,
                                background: isOn ? 'var(--primary-light)' : 'var(--bg)',
                                color: isOn ? 'var(--primary)' : 'var(--text-muted)',
                                border: `1px solid ${isOn ? 'var(--primary)40' : 'var(--border)'}`,
                                opacity: isOn ? 1 : 0.35,
                              }}>{mt.icon} {mt.label}</span>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Card Footer */}
                  <div style={{ padding: '12px 20px', borderTop: '1px solid var(--border)', display: 'flex', gap: 8 }}>
                    <button className="btn btn-secondary btn-sm" style={{ flex: 1 }} onClick={() => openEdit(plan)}>
                      <Edit3 size={12} /> Edit Plan
                    </button>
                    <button className="btn btn-danger btn-sm" onClick={() => handleDelete(plan)}>
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showModal && (
        <PlanModal plan={editingPlan} onClose={() => { setShowModal(false); setEditingPlan(null); }} onSaved={handleSaved} />
      )}
    </div>
  );
}

function PlanModal({ plan, onClose, onSaved }) {
  const isEdit = !!plan;
  const [form, setForm] = useState(() => {
    if (plan) {
      return {
        name: plan.name || '',
        slug: plan.slug || '',
        description: plan.description || '',
        price: { monthly: plan.price?.monthly ?? '', yearly: plan.price?.yearly ?? '', currency: plan.price?.currency || 'MYR' },
        limits: {
          messagesPerMonth: plan.limits?.messagesPerMonth ?? '',
          aiRepliesPerMonth: plan.limits?.aiRepliesPerMonth ?? '',
          maxAgents: plan.limits?.maxAgents ?? '',
          maxChannels: plan.limits?.maxChannels ?? '',
          maxWorkflows: plan.limits?.maxWorkflows ?? 1,
          maxPrompts: plan.limits?.maxPrompts ?? 1,
        },
        features: (plan.features || []).join('\n'),
        allowedMediaTypes: plan.allowedMediaTypes || ['image', 'audio', 'pdf', 'document'],
        allowedChannels: plan.allowedChannels || ['whatsapp', 'telegram', 'facebook', 'instagram', 'webchat'],
        allowedDbTypes: plan.allowedDbTypes || [],
        channelTypeLimits: { ...emptyChannelTypeLimits, ...(plan.channelTypeLimits || {}) },
        isActive: plan.isActive !== false,
        sortOrder: plan.sortOrder || 0,
      };
    }
    return { ...emptyPlan, price: { monthly: '', yearly: '', currency: 'MYR' }, allowedMediaTypes: [...emptyPlan.allowedMediaTypes], channelTypeLimits: { ...emptyChannelTypeLimits } };
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setForm(prev => ({ ...prev, [name]: checked }));
    } else if (name.startsWith('price.')) {
      const key = name.split('.')[1];
      setForm(prev => ({ ...prev, price: { ...prev.price, [key]: value } }));
    } else if (name.startsWith('limits.')) {
      const key = name.split('.')[1];
      setForm(prev => ({ ...prev, limits: { ...prev.limits, [key]: value } }));
    } else {
      setForm(prev => ({ ...prev, [name]: value }));
    }
  };

  const toggleMediaType = (key) => {
    setForm(prev => ({
      ...prev,
      allowedMediaTypes: prev.allowedMediaTypes.includes(key)
        ? prev.allowedMediaTypes.filter(t => t !== key)
        : [...prev.allowedMediaTypes, key],
    }));
  };

  const toggleChannel = (key) => {
    setForm(prev => ({
      ...prev,
      allowedChannels: prev.allowedChannels.includes(key)
        ? prev.allowedChannels.filter(c => c !== key)
        : [...prev.allowedChannels, key],
    }));
  };

  const toggleDbType = (key) => {
    setForm(prev => ({
      ...prev,
      allowedDbTypes: prev.allowedDbTypes.includes(key)
        ? prev.allowedDbTypes.filter(d => d !== key)
        : [...prev.allowedDbTypes, key],
    }));
  };

  const setChannelTypeLimit = (key, val) => {
    const num = Math.max(0, parseInt(val) || 0);
    setForm(prev => ({ ...prev, channelTypeLimits: { ...prev.channelTypeLimits, [key]: num } }));
  };

  const handleNameChange = (e) => {
    const name = e.target.value;
    setForm(prev => ({
      ...prev,
      name,
      slug: isEdit ? prev.slug : name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, ''),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.slug) { toast.error('Name and slug are required'); return; }

    const payload = {
      name: form.name,
      slug: form.slug,
      description: form.description,
      price: {
        monthly: Number(form.price.monthly) || 0,
        yearly: Number(form.price.yearly) || 0,
        currency: form.price.currency || 'MYR',
      },
      limits: {
        messagesPerMonth: Number(form.limits.messagesPerMonth) || 0,
        aiRepliesPerMonth: Number(form.limits.aiRepliesPerMonth) || 0,
        maxAgents: Number(form.limits.maxAgents) || 0,
        maxChannels: Number(form.limits.maxChannels) || 0,
        maxWorkflows: Number(form.limits.maxWorkflows) || 1,
        maxPrompts: Number(form.limits.maxPrompts) || 1,
      },
      features: form.features.split('\n').map(f => f.trim()).filter(Boolean),
      allowedMediaTypes: form.allowedMediaTypes,
      allowedChannels: form.allowedChannels,
      allowedDbTypes: form.allowedDbTypes,
      channelTypeLimits: form.channelTypeLimits,
      isActive: form.isActive,
      sortOrder: Number(form.sortOrder) || 0,
    };

    setSubmitting(true);
    try {
      if (isEdit) {
        await adminAPI.updatePlan(plan._id, payload);
        toast.success('Plan updated');
      } else {
        await adminAPI.createPlan(payload);
        toast.success('Plan created');
      }
      onSaved();
    } catch (_) {}
    finally { setSubmitting(false); }
  };

  const SectionLabel = ({ children }) => (
    <div style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)', marginBottom: 8, marginTop: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
      <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
      {children}
      <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
    </div>
  );

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 580 }}>
        <div className="modal-header">
          <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Activity size={18} style={{ color: 'var(--primary)' }} />
            {isEdit ? `Edit — ${plan.name}` : 'Create New Plan'}
          </h3>
          <button className="btn-icon" onClick={onClose}><X size={18} /></button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14, maxHeight: '72vh', overflowY: 'auto', padding: '16px 20px' }}>

            {/* Basic Info */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label className="form-label">Plan Name *</label>
                <input className="form-input" name="name" value={form.name} onChange={handleNameChange} placeholder="e.g. Starter" />
              </div>
              <div>
                <label className="form-label">Slug *</label>
                <input className="form-input" name="slug" value={form.slug} onChange={handleChange} placeholder="e.g. starter" />
              </div>
            </div>

            <div>
              <label className="form-label">Description</label>
              <input className="form-input" name="description" value={form.description} onChange={handleChange} placeholder="Brief plan description" />
            </div>

            <SectionLabel>Pricing (MYR)</SectionLabel>

            {/* Currency row */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
              <div>
                <label className="form-label">Currency</label>
                <select className="form-input" name="price.currency" value={form.price.currency} onChange={handleChange}>
                  <option value="MYR">MYR — Malaysian Ringgit</option>
                  <option value="USD">USD — US Dollar</option>
                  <option value="SGD">SGD — Singapore Dollar</option>
                </select>
              </div>
              <div>
                <label className="form-label">Monthly Price (sen)</label>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: '0.8rem', color: 'var(--text-muted)', fontWeight: 600 }}>RM</span>
                  <input className="form-input" name="price.monthly" type="number" value={form.price.monthly} onChange={handleChange}
                    placeholder="0" style={{ paddingLeft: 34 }} />
                </div>
                {form.price.monthly > 0 && (
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 3 }}>
                    = RM {(Number(form.price.monthly) / 100).toFixed(2)} / month
                  </div>
                )}
              </div>
              <div>
                <label className="form-label">Yearly Price (sen)</label>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: '0.8rem', color: 'var(--text-muted)', fontWeight: 600 }}>RM</span>
                  <input className="form-input" name="price.yearly" type="number" value={form.price.yearly} onChange={handleChange}
                    placeholder="0" style={{ paddingLeft: 34 }} />
                </div>
                {form.price.yearly > 0 && (
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 3 }}>
                    = RM {(Number(form.price.yearly) / 100).toFixed(2)} / year
                  </div>
                )}
              </div>
            </div>
            <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', background: 'var(--bg)', padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)' }}>
              Enter prices in sen (smallest unit). Example: RM 99.00 = 9900 sen. Enter 0 for a free plan.
            </div>

            <SectionLabel>Plan Limits</SectionLabel>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              {[
                { name: 'limits.messagesPerMonth', label: 'Messages / Month', placeholder: 'e.g. 2000  (-1 = unlimited)' },
                { name: 'limits.aiRepliesPerMonth', label: 'AI Replies / Month', placeholder: 'e.g. 1000  (-1 = unlimited)' },
                { name: 'limits.maxAgents', label: 'Max Agents', placeholder: 'e.g. 3  (-1 = unlimited)' },
                { name: 'limits.maxChannels', label: 'Max Channels', placeholder: 'e.g. 2  (-1 = unlimited)' },
                { name: 'limits.maxWorkflows', label: 'Max Workflows', placeholder: 'e.g. 1  (-1 = unlimited)' },
                { name: 'limits.maxPrompts', label: 'Max Prompts', placeholder: 'e.g. 1  (-1 = unlimited)' },
              ].map(f => (
                <div key={f.name}>
                  <label className="form-label">{f.label}</label>
                  <input className="form-input" name={f.name} type="number"
                    value={f.name.startsWith('limits.') ? form.limits[f.name.split('.')[1]] : ''}
                    onChange={handleChange} placeholder={f.placeholder} />
                </div>
              ))}
            </div>

            <SectionLabel>Features</SectionLabel>

            <div>
              <textarea className="form-input" name="features" value={form.features} onChange={handleChange}
                rows={4} placeholder={"2,000 messages/month\n1,000 AI replies\n3 agents\nEmail support"} style={{ resize: 'vertical' }} />
              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 4 }}>One feature per line — displayed on plan cards</div>
            </div>

            <SectionLabel>Allowed Media Types</SectionLabel>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {ALL_MEDIA_TYPES.map(mt => {
                const isOn = form.allowedMediaTypes.includes(mt.key);
                return (
                  <div key={mt.key} onClick={() => toggleMediaType(mt.key)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6, padding: '7px 12px', borderRadius: 8, cursor: 'pointer',
                      border: `1.5px solid ${isOn ? 'var(--primary)' : 'var(--border)'}`,
                      background: isOn ? 'var(--primary-light)' : 'transparent',
                      transition: 'all 0.15s', userSelect: 'none',
                    }}>
                    {mt.icon}
                    <span style={{ fontSize: '0.8rem', fontWeight: 500 }}>{mt.label}</span>
                    <div style={{ width: 15, height: 15, borderRadius: 3, border: `1.5px solid ${isOn ? 'var(--primary)' : 'var(--border)'}`, background: isOn ? 'var(--primary)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {isOn && <Check size={9} style={{ color: '#fff' }} />}
                    </div>
                  </div>
                );
              })}
            </div>

            <SectionLabel>Allowed Channels &amp; Per-Type Limits</SectionLabel>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {ALL_CHANNELS.map(ch => {
                const isOn = form.allowedChannels.includes(ch.key);
                const typeLimit = form.channelTypeLimits[ch.key] || 0;
                return (
                  <div key={ch.key} style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 8,
                    border: `1.5px solid ${isOn ? ch.color : 'var(--border)'}`,
                    background: isOn ? ch.color + '10' : 'var(--bg)',
                    transition: 'all 0.15s',
                  }}>
                    {/* Toggle */}
                    <div onClick={() => toggleChannel(ch.key)} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', flex: 1, userSelect: 'none' }}>
                      <span style={{ width: 9, height: 9, borderRadius: '50%', background: isOn ? ch.color : 'var(--border)', display: 'inline-block', flexShrink: 0 }} />
                      <span style={{ fontSize: '0.82rem', fontWeight: 600, color: isOn ? ch.color : 'var(--text-muted)' }}>{ch.label}</span>
                      <div style={{ width: 15, height: 15, borderRadius: 3, border: `1.5px solid ${isOn ? ch.color : 'var(--border)'}`, background: isOn ? ch.color : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', marginLeft: 2 }}>
                        {isOn && <Check size={9} style={{ color: '#fff' }} />}
                      </div>
                    </div>
                    {/* Per-type limit input */}
                    {isOn && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                        <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>Max allowed:</span>
                        <input
                          type="number" min="0"
                          value={typeLimit === 0 ? '' : typeLimit}
                          onChange={e => setChannelTypeLimit(ch.key, e.target.value)}
                          placeholder="∞"
                          style={{ width: 60, padding: '4px 8px', fontSize: '0.8rem', fontWeight: 600, border: `1.5px solid ${ch.color}60`, borderRadius: 6, outline: 'none', background: 'var(--card-bg)', color: ch.color, textAlign: 'center' }}
                        />
                        <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{typeLimit === 0 ? '(unlimited)' : `channel${typeLimit !== 1 ? 's' : ''}`}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', padding: '4px 8px', background: 'var(--bg)', borderRadius: 6, border: '1px solid var(--border)' }}>
              Set max per channel type (e.g. Instagram = 1, WhatsApp = 2). Leave blank or 0 for unlimited (up to total max channels).
            </div>

            <SectionLabel>Database Integrations</SectionLabel>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {ALL_DB_TYPES.map(db => {
                const isOn = form.allowedDbTypes.includes(db.key);
                return (
                  <div key={db.key} onClick={() => toggleDbType(db.key)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6, padding: '7px 12px', borderRadius: 8, cursor: 'pointer',
                      border: `1.5px solid ${isOn ? db.color : 'var(--border)'}`,
                      background: isOn ? db.color + '15' : 'transparent',
                      transition: 'all 0.15s', userSelect: 'none',
                    }}>
                    <Database size={12} style={{ color: isOn ? db.color : 'var(--text-muted)' }} />
                    <span style={{ fontSize: '0.8rem', fontWeight: 500, color: isOn ? db.color : 'var(--text-muted)' }}>{db.label}</span>
                    <div style={{ width: 15, height: 15, borderRadius: 3, border: `1.5px solid ${isOn ? db.color : 'var(--border)'}`, background: isOn ? db.color : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {isOn && <Check size={9} style={{ color: '#fff' }} />}
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Leave all unchecked to disable database integrations on this plan</div>

            <SectionLabel>Settings</SectionLabel>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label className="form-label">Sort Order</label>
                <input className="form-input" name="sortOrder" type="number" value={form.sortOrder} onChange={handleChange} placeholder="0" />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingTop: 22 }}>
                <input type="checkbox" id="isActive" name="isActive" checked={form.isActive} onChange={handleChange} style={{ width: 16, height: 16 }} />
                <label htmlFor="isActive" style={{ fontSize: '0.85rem', cursor: 'pointer', fontWeight: 500 }}>Active (visible to merchants)</label>
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? 'Saving…' : isEdit ? 'Update Plan' : 'Create Plan'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AdminPlans;
